import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { MenuDrawer } from './MenuDrawer';
import { SyncBanner } from './SyncBanner';
import { UpdateBanner } from './UpdateBanner';
import { ConflictModal } from './ConflictModal';
import { PushToast } from './PushToast';
import { PushPermissionPrompt } from './PushPermissionPrompt';
import {
  getPushPermissionState,
  onForegroundMessage,
  refreshPushTokenIfGranted,
} from '../../lib/fcm';
import { shouldShowPushPrompt } from '../../lib/push-onboarding';
import { cachePushNotification, listCachedPushNotifications } from '../../lib/push-cache';
import { getUnreadCount } from '../../lib/driver-notifications-api';
import { invalidate } from '../../lib/cache';
import { notifyTripRefresh, type TripRefreshReason } from '../../lib/trip-refresh';
import { discardPendingMutationsForBooking } from '../../lib/sync';

/** Per-route header overrides. Routes set this via `useSetPageHeader()` so
 *  AppShell stays the single mount point for the AppHeader chrome. Nulling
 *  the config restores the default menu+bell layout. */
export type PageHeaderConfig = {
  backTo?: string | -1;
  title?: string;
  hideBell?: boolean;
};

type PageHeaderContextValue = {
  setConfig: (config: PageHeaderConfig | null) => void;
};

const PageHeaderContext = createContext<PageHeaderContextValue>({ setConfig: () => {} });

/** Routes call this in a useEffect to override the AppHeader for their screen.
 *  The cleanup automatically restores the default header on unmount. */
export function useSetPageHeader(config: PageHeaderConfig | null, deps: ReadonlyArray<unknown> = []) {
  const { setConfig } = useContext(PageHeaderContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setConfig(config);
    return () => setConfig(null);
  }, deps);
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pushMsg, setPushMsg] = useState<{ title: string; body: string; link?: string } | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [headerConfig, setHeaderConfig] = useState<PageHeaderConfig | null>(null);
  const [pushPromptOpen, setPushPromptOpen] = useState(false);

  useEffect(() => {
    // Open the modal once on first mount if the driver hasn't been onboarded
    // AND the browser permission is still 'default'. shouldShowPushPrompt()
    // gates on both. After dismiss/grant, markPushOnboarded() keeps it closed
    // for the rest of the session.
    if (shouldShowPushPrompt(getPushPermissionState())) {
      setPushPromptOpen(true);
    }
    // Silently re-register the FCM token if permission is already granted —
    // catches token rotation between sessions. No UI side effects.
    void refreshPushTokenIfGranted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshUnread = useCallback(async () => {
    // Server is canonical (DriverNotification table). IDB cache is consulted
    // only when the network call fails — it covers offline + SW foreground
    // pushes that haven't been confirmed by the server yet.
    try {
      const count = await getUnreadCount();
      setUnreadCount(count);
    } catch {
      try {
        const rows = await listCachedPushNotifications();
        setUnreadCount(rows.filter(r => !r.readAt).length);
      } catch {
        setUnreadCount(0);
      }
    }
  }, []);

  useEffect(() => {
    void refreshUnread();
  }, [refreshUnread, location.pathname]);

  useEffect(() => {
    const unsub = onForegroundMessage((msg) => {
      void cachePushNotification(msg).then(refreshUnread);
      setPushMsg({ title: msg.title, body: msg.body, link: msg.data?.url });

      const type = msg.data?.type;
      const bookingId = msg.data?.bookingId;
      const reasonByType: Record<string, TripRefreshReason> = {
        BOOKING_ALLOCATED: 'allocated',
        BOOKING_UNASSIGNED: 'unassigned',
        BOOKING_CANCELLED: 'cancelled',
      };
      const reason = type ? reasonByType[type] : undefined;
      if (reason) {
        void invalidate('/driver-app/me/trips/today');
        if (bookingId) void invalidate(`/driver-app/me/trips/${bookingId}`);
        if (bookingId && (reason === 'unassigned' || reason === 'cancelled')) {
          void discardPendingMutationsForBooking(bookingId);
        }
        notifyTripRefresh({ reason, bookingId });
      }
    });
    return unsub;
  }, [refreshUnread]);

  const ctxValue = useMemo<PageHeaderContextValue>(() => ({ setConfig: setHeaderConfig }), []);

  return (
    <PageHeaderContext.Provider value={ctxValue}>
      <div
        className="driver-frame flex flex-col w-full"
        style={{
          // Match #root: viewport minus the safe-area insets body pads with,
          // so short pages don't overflow into an unnecessary scroll.
          minHeight: 'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
          maxWidth: 440,
        }}
      >
        <UpdateBanner />
        <AppHeader
          onMenuOpen={() => setDrawerOpen(true)}
          onBellClick={() => navigate('/notifications')}
          unread={unreadCount}
          backTo={headerConfig?.backTo}
          title={headerConfig?.title}
          hideBell={headerConfig?.hideBell}
        />
        <SyncBanner />
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
        <MenuDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
        <ConflictModal />
        <PushToast msg={pushMsg} onDismiss={() => setPushMsg(null)} />
        <PushPermissionPrompt
          open={pushPromptOpen}
          onClose={() => setPushPromptOpen(false)}
        />
      </div>
    </PageHeaderContext.Provider>
  );
}
